import { QuestionDataFilters, QuestionInsightFilterType, QuestionType, Tag } from "types/questions";
import { TimeviewBucketMode, TimeviewBucketModeWithNone, TimeviewViewType } from "types/charts";

import { ApiDateFilter } from "types/dates";
import { DateLike } from "utils";
import { NullsAsOptionals } from "types/type-utils";
import { QuestionInsightDisplayMode } from "consts/QuestionInsightDisplayModes";
import { QuestionRoleTypeTerm } from "consts/QuestionRoleType";

// *********************************
// Core Dashlet Types
// *********************************

export const dashletTypes = [
  "QuestionResultsDashlet",
  "QuestionCompareDashlet",
  "QuestionInsightsDashlet",
  "QuestionCompareListDashlet",
  "QuestionListDashlet",
  "CommentDashlet",
  "TimeviewResultsDashlet",
  "MultiTimeviewDashlet",
  "ScorecardDashlet",
  "ScoreResultsDashlet",
] as const;

/**
 * A union representing the supported Dashlet types.
 */
export type DashletType = (typeof dashletTypes)[number];

/**
 * A union representing dashlets which can be "created" from directly on the dashboard.
 * Ideally, this is all dashlets, but while there are some we cannot create this list
 * is useful to make sure we account for any added dashlets.
 */
export type CreateableDashletType = Exclude<DashletType, "ScorecardDashlet">;

/**
 * Base building block for a Dashlet object.
 * This is type, config, and all other common top level properties.
 */
type DashletBase<DashType extends DashletType, Config> = {
  type: DashType;
  config: Config;
  dashboardUuid: string;
  dashletUuid: string;
  row: number;
  col: number;
  lockTime: Date | null;
  lockData: string | null; // FIXME once requirement known
};

export type Dashlet =
  | QuestionResultsDashlet
  | QuestionCompareDashlet
  | QuestionInsightsDashlet
  | QuestionCompareListDashlet
  | QuestionListDashlet
  | CommentDashlet
  | TimeviewResultsDashlet
  | MultiTimeviewDashlet
  | ScorecardDashlet
  | ScoreResultsDashlet;

export type DashletCreate = AsDashletCreate<Dashlet> & {
  dashboardUuid?: string;
  newDashboardName?: string;
  column?: number;
};

// *********************************
// Question Compare Dashlet Types
// *********************************

export type QuestionCompareDashletConfig = DashletConfigBase &
  QuestionDataFilters &
  DashletApiDateFilters & {
    qid: number | string;
    cmpQid: number | string;
    valueGroupingUri?: string | null;
    secondaryValueGroupingUri?: string | null;
    // For now we dont take this attribute into account and render the default chart
    displayMode?: "stacked" | "table" | "horizontal" | "sankey" | null;
    // TODO: This should be its own type
    percentageOption: "answer" | "overall";
  };

export type QuestionCompareDashlet = DashletBase<"QuestionCompareDashlet", QuestionCompareDashletConfig>;

// *********************************
// Question Results Dashlet Types
// *********************************

// Dashlet supported question types
export type DashletSupportedQuestionType = Extract<QuestionType, "radio" | "checkbox">;

export type QuestionResultsDashletConfig = DashletConfigBase &
  QuestionDataFilters &
  DashletApiDateFilters & {
    qid: number | string;
    valueGroupingUri: string | null;
    chartDisplayMode?: ChartDisplayMode | null;
    questionType?: DashletSupportedQuestionType;
    rebaseOptOut?: boolean;
  } & Pick<DashletApiDateFilters, "daysBefore">;

export type QuestionResultsDashlet = DashletBase<"QuestionResultsDashlet", QuestionResultsDashletConfig>;

// *********************************
// Question Insights Dashlet Types
// *********************************

export type QuestionInsightsDashletConfig = DashletConfigBase &
  QuestionDataFilters &
  DashletApiDateFilters & {
    qid: number | string;
    count: number | null;
    mode: QuestionInsightDisplayMode | null;
    insightFilterType: QuestionInsightFilterType | null;
    /**
     * Note that this represents the *ID* of the item, for example, "optionQuestionResponse/625781".
     * In some cases we need the associated label, for example "Income", and must look this up.
     */
    insightFilterVal: string | null;
    valueGroupingUri: string | null;
  } & Pick<DashletApiDateFilters, "daysBefore">;

export type QuestionInsightsDashlet = DashletBase<"QuestionInsightsDashlet", QuestionInsightsDashletConfig>;

export type QuestionListAdditionalData = {
  tagsFull?: Tag[] | null;
};

export type QuestionCompareListAdditionalData = QuestionListAdditionalData & {
  qtext?: string | null;
};

// *********************************
// Question List Dashlet Types
// *********************************

// A filter param we are saving to Dashlet Configs
// TODO: Consider matching this to api types. See question-types.QuestionTypeFilter
export type DashletQuestionListTypeFilter = "custom" | "syndicated" | "";

export type QuestionListDashletBaseConfig = {
  count: string | null;
  filters?: {
    completedBefore: DateLike | null;
    isWatchlist: boolean;
    launchedAfter: DateLike | null;
    questionRole: QuestionRoleTypeTerm | null;
    questionType: DashletQuestionListTypeFilter | null;
    search: string | null;

    // this is unused at the moment because IS2 search works based off of string tag names.
    // the dashlet config has integer IDs
    tags: number[];

    // The following exist in the config, but are NOT supported by IS2 search
    feed: string | null;
    target?: string | number | null;
    isAccount: boolean;
  };
};

export type QuestionCompareListDashletConfig = Pick<DashletConfigBase, "dashletName"> &
  QuestionListDashletBaseConfig & {
    qid: number | string;
    weightingMode: string | null;
    // This may become a standard property across many dashlet types
    // If so, we could move this into a base type and make it generic - `additionData: T`
    additionalData?: QuestionCompareListAdditionalData;
  };

export type QuestionCompareListDashlet = DashletBase<"QuestionCompareListDashlet", QuestionCompareListDashletConfig>;

export type QuestionListDashletConfig = Pick<DashletConfigBase, "dashletName"> &
  QuestionListDashletBaseConfig & {
    sorts: string[]; // 'newest' | 'oldest' | 'responses' | 'relevance'
    // This may become a standard property across many dashlet types
    // If so, we could move this into a base type and make it generic - `additionData: T`
    additionalData?: QuestionListAdditionalData;
  };

export type QuestionListDashlet = DashletBase<"QuestionListDashlet", QuestionListDashletConfig>;

// *********************************
// Comment Dashlet Types
// *********************************

// We may have to further break down DashletConfigBase
export type CommentDashletConfig = Pick<DashletConfigBase, "dashletName"> & {
  commentText: string | null;
};

export type CommentDashlet = DashletBase<"CommentDashlet", CommentDashletConfig>;

// *********************************
// Timeview Results Dashlet Types
// *********************************

export type TimeviewResultsDashletConfig = DashletConfigBase &
  QuestionDataFilters &
  DashletApiDateFilters & {
    // question based
    qid: number | string;
    valueGroupingUri: string | null;

    // timeview based
    viewType: TimeviewViewType;
    bucketMode: TimeviewBucketMode;
  };

export type TimeviewResultsDashlet = DashletBase<"TimeviewResultsDashlet", TimeviewResultsDashletConfig>;

// *********************************
// Multi Timeview Dashlet Types
// *********************************

export type MultiTimeviewDashletConfig = DashletConfigBase &
  DashletApiDateFilters & {
    uuid: string;
    grain: TimeviewBucketMode;
  };

export type MultiTimeviewDashlet = DashletBase<"MultiTimeviewDashlet", MultiTimeviewDashletConfig>;

// *********************************
// Scorecard Dashlet Types
// *********************************

export type ScorecardDashletConfig = DashletConfigBase & {
  uuid: string;
  sortOrder?: "ascending" | "descending";
  sortColumn?: string;
  rowLimit?: string;
};

export type ScorecardDashlet = DashletBase<"ScorecardDashlet", ScorecardDashletConfig>;

// *********************************
// Score Results Dashlet Types
// *********************************

export type ScoreResultsDashletConfig = DashletConfigBase &
  DashletApiDateFilters &
  QuestionDataFilters & {
    qid: number | string;
    bucketMode: TimeviewBucketModeWithNone;
    hideTimeview: boolean;
    timeviewAvailable: boolean;
    scoreUri: string;
  };

export type ScoreResultsDashlet = DashletBase<"ScoreResultsDashlet", ScoreResultsDashletConfig>;

// *********************************
// Shared Dashlet Config Types
// *********************************
/**
 * Common properties across all dashlet conResultsfigs.
 */

export type DashletReorderData = {
  dashboardUuid: string;
  dashletUuid: string;
  config: {
    row: number;
    col: number;
  };
};

type DashletConfigBase = {
  dashletName: string | null;
  dashletComments: string | null;
};

/**
 * The list of supported chart display modes.
 */
export const chartDisplayModes = [
  "donut_chart",
  "horizontal_bar_chart",
  "stacked_bar_chart",
  "vertical_bar_chart",
] as const;

/**
 * A union representing chart display mode options.
 */
export type ChartDisplayMode = (typeof chartDisplayModes)[number];

export type DashletApiDateFilters = ApiDateFilter;

/**
 * Dashlets that support GlobalFilters.
 */
export type GlobalFilterEnabledDashlets =
  | QuestionCompareDashlet
  | QuestionResultsDashlet
  | TimeviewResultsDashlet
  | ScoreResultsDashlet;

/**
 * Dashlet global filters type for saving applied GlobalFilters to dashlets.
 * - applied filters will have a string/number value.
 * - not-applied filters will have an undefined value.
 * - clear-current-value filters will have a null value.
 */
export type DashletGlobalFiltersSaveBulk = Partial<ApiDateFilter> & {
  networkUuid?: string | null;
  segmentUuid?: string | null;
  weightingMode?: string | null;
  targetId?: number | string | null;
};

// *********************************
// Type Helpers
// *********************************

/**
 * Generates a union of DashCreate types from the underlying Dashlet type.
 * Essentially, a "Create" Dashlet is just the type and config of a Dashlet type.
 * https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types
 */
type AsDashletCreate<T extends Dashlet> = T extends unknown
  ? { type: T["type"]; config: NullsAsOptionals<T["config"]> }
  : never;
/**
 * Extracts the config of the provided DashletType.
 */
export type DashletConfig<T extends DashletType> = Extract<Dashlet, { type: T }>["config"];

// *********************************
// Ensure all types are accounted for
// *********************************
/**
 * This snippet is used to ensure that `Dashlet` includes an entry for every `DashletType`.
 * If you are getting a compiler error here something is out of sync between them.
 * In VSCode if you hover the `VerifyDashlet` type you'll likely see the missing option.
 *
 * https://www.zhenghao.io/posts/ts-never#how-to-check-for-never
 */
type IsNever<T> = [T] extends [never] ? true : false;
type VerifyDashlet = Exclude<DashletType, Dashlet["type"]>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isDashletExhaustive: IsNever<VerifyDashlet> = true;
